import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import CreditIcon from '../../CreditIcon';
import '../../../styles/FactionRosterExperience.css';
import { getBrandBadgeClass } from '../../../utils/brandBadge';
import {
  buildFactionMemberFields,
  buildFactionWrestlersFromSelection,
} from '../../../utils/factionWrestlers';
import { canCreateFaction } from '../../../utils/seasonGate';

const FactionSummary = ({
  selectedWrestlers,
  gameMode,
  onBack,
  userProfile,
  onComplete,
  existingFactionId = null,
  roster = null,
  rosterName: propRosterName = null,
}) => {
  const {
    maxWrestlers = 6,
    maxCredits = 300,
    minMaleWrestlers = 0,
    minFemaleWrestlers = 0,
    name: rosterNameFromObj = '',
  } = roster || {};

  const [factionName, setFactionName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [wrestlerScores, setWrestlerScores] = useState({});
  const [rosterName, setRosterName] = useState(propRosterName || rosterNameFromObj);
  const [rosterList, setRosterList] = useState([]);

  useEffect(() => {
    if (roster) {
      setRosterList([roster]);
      setRosterName(roster.name);
      return;
    }

    if (propRosterName !== null) {
      setRosterName(propRosterName);
      return;
    }

    async function fetchRosters() {
      try {
        const db = getFirestore();
        const snap = await getDocs(query(collection(db, 'rosters'), orderBy('createdAt', 'desc')));
        const userRosters = snap.docs
          .map((document) => ({ id: document.id, ...document.data() }))
          .filter((item) => item.participantIds?.includes(userProfile.uid) || item.ownerUid === userProfile.uid);
        const mostRecent = userRosters.length > 0 ? [userRosters[0]] : [];
        setRosterList(mostRecent);
        setRosterName(mostRecent[0]?.name || '');
      } catch (fetchError) {
        setRosterList([]);
        setRosterName('');
      }
    }

    fetchRosters();
  }, [propRosterName, roster, userProfile?.uid]);

  useEffect(() => {
    const fetchScores = async () => {
      const db = getFirestore();
      const newScores = {};

      for (const wrestler of selectedWrestlers) {
        const docRef = doc(db, 'wrestlers', wrestler.id);
        const snapshot = await getDoc(docRef);
        if (snapshot.exists()) {
          newScores[wrestler.id] = snapshot.data().score || 0;
        }
      }

      setWrestlerScores(newScores);
    };

    fetchScores();
  }, [selectedWrestlers]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isSubmitting) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isSubmitting]);

  const totalCredits = selectedWrestlers.reduce((sum, wrestler) => sum + (wrestler.price || 0), 0);
  const creditsLeft = maxCredits - totalCredits;
  const maleCount = selectedWrestlers.filter((wrestler) => wrestler.gender === 'Uomo').length;
  const femaleCount = selectedWrestlers.filter((wrestler) => wrestler.gender === 'Donna').length;
  const canSubmit = (
    selectedWrestlers.length > 0 &&
    selectedWrestlers.length <= maxWrestlers &&
    maleCount >= minMaleWrestlers &&
    femaleCount >= minFemaleWrestlers &&
    totalCredits <= maxCredits
  );

  const selectionProgress = maxWrestlers > 0 ? Math.min((selectedWrestlers.length / maxWrestlers) * 100, 100) : 0;
  const creditsProgress = maxCredits > 0 ? Math.min((totalCredits / maxCredits) * 100, 100) : 0;

  const summaryItems = useMemo(() => ([
    {
      label: 'Wrestler scelti',
      value: `${selectedWrestlers.length}/${maxWrestlers}`,
      helper: selectedWrestlers.length === maxWrestlers ? 'Fazione completa' : `${Math.max(maxWrestlers - selectedWrestlers.length, 0)} posti rimasti`,
      icon: 'bi-people-fill',
    },
    {
      label: 'Budget residuo',
      value: `${creditsLeft}`,
      helper: `${totalCredits}/${maxCredits} crediti usati`,
      icon: 'bi-wallet2',
    },
    {
      label: 'Vincoli genere',
      value: `${maleCount}/${minMaleWrestlers} - ${femaleCount}/${minFemaleWrestlers}`,
      helper: maleCount >= minMaleWrestlers && femaleCount >= minFemaleWrestlers ? 'Requisiti rispettati' : 'Controlla i minimi richiesti',
      icon: 'bi-person-badge',
    },
  ]), [creditsLeft, femaleCount, maleCount, maxCredits, maxWrestlers, minFemaleWrestlers, minMaleWrestlers, selectedWrestlers.length, totalCredits]);

  const selectedBrands = useMemo(() => {
    const counts = {};
    selectedWrestlers.forEach((wrestler) => {
      const key = wrestler.brand || wrestler.roster || 'N/A';
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts);
  }, [selectedWrestlers]);

  const handleSubmit = async () => {
    if (!userProfile?.uid) {
      setError('Profilo utente non disponibile. Riprova più tardi.');
      return;
    }
    if (!existingFactionId && !canCreateFaction(userProfile?.email)) {
      setError('La creazione delle fazioni e bloccata fino al 1 maggio alle 12:00.');
      return;
    }
    if (!factionName.trim()) {
      setError('Inserisci un nome per la tua fazione');
      return;
    }
    if (factionName.length < 3) {
      setError('Il nome deve essere di almeno 3 caratteri');
      return;
    }
    if (!canSubmit) {
      setError('La selezione non rispetta i limiti del roster');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const db = getFirestore();

      let selectedRoster = roster;
      if (!selectedRoster && rosterName) {
        selectedRoster = rosterList.find((item) => item.name === rosterName);
      }

      if (!selectedRoster) {
        setError('Roster non valido o non selezionato');
        setIsSubmitting(false);
        return;
      }

      const dataInizioPunteggio = selectedRoster.dataInizioPunteggio || serverTimestamp();
      const rosterId = selectedRoster.id;
      const factionWrestlers = buildFactionWrestlersFromSelection(selectedWrestlers, wrestlerScores);
      const memberFields = buildFactionMemberFields(factionWrestlers);
      const factionData = {
        name: factionName,
        creditsLeft,
        accumulatedPoints: 0,
        ...memberFields,
        rosterId,
        dataInizioPunteggio,
      };

      if (existingFactionId) {
        await updateDoc(doc(db, 'factions', existingFactionId), {
          ...factionData,
          costs: deleteField(),
          wrestlerIds: deleteField(),
          updatedAt: serverTimestamp(),
        });

        await updateDoc(doc(db, 'users', userProfile.uid), {
          factionIds: arrayUnion(existingFactionId),
          rosterIds: arrayUnion(rosterId),
        });

        setSuccessMessage('Fazione aggiornata con successo!');
        setTimeout(() => onComplete(), 1200);
      } else {
        const factionRef = await addDoc(collection(db, 'factions'), {
          ...factionData,
          createdAt: serverTimestamp(),
          ownerUid: userProfile.uid,
        });

        await updateDoc(doc(db, 'users', userProfile.uid), {
          factionIds: arrayUnion(factionRef.id),
          rosterIds: arrayUnion(rosterId),
        });

        setSuccessMessage('Fazione creata con successo!');
        setTimeout(() => onComplete(), 1500);
      }
    } catch (submitError) {
      console.error(submitError);
      setError('Errore durante il salvataggio. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderWrestlerImage = (wrestler, size = '48px') => {
    if (wrestler.imageUrl) {
      return (
        <img
          src={wrestler.imageUrl}
          alt={wrestler.name}
          style={{
            width: size,
            height: size,
            objectFit: 'cover',
            borderRadius: '14px',
          }}
        />
      );
    }

    return (
      <div
        className={`d-flex align-items-center justify-content-center ${wrestler.gender === 'Uomo' ? 'bg-danger text-white' : 'female text-white'}`}
        style={{ width: size, height: size, borderRadius: '14px' }}
      >
        {wrestler.gender === 'Uomo' ? 'M' : 'F'}
      </div>
    );
  };

  const InfoCard = ({ title, value, icon, helper, accent = false }) => (
    <div className={`frx-feature-card ${accent ? 'border border-brand-secondary' : ''}`}>
      <span className="frx-label">
        <i className={`bi ${icon} me-2`}></i>
        {title}
      </span>
      <div className={`frx-value ${accent ? 'text-brand-secondary' : ''}`}>{value}</div>
      {helper ? <div className="frx-helper mt-1">{helper}</div> : null}
    </div>
  );

  return (
    <div className={`frx-page min-vh-100 py-4 ${isSubmitting ? 'pe-none' : ''}`}>
      {isSubmitting && (
        <div className="overlay d-flex align-items-center justify-content-center">
          <div className="text-center frx-surface p-4 rounded-4 shadow-lg border border-brand-secondary">
            <div className="spinner-border text-brand-secondary mb-3" role="status"></div>
            <h5 className="brand-title">Salvataggio in corso...</h5>
            <p className="text-muted-brand">Attendi qualche istante</p>
          </div>
        </div>
      )}

      <div className="container-fluid px-3 px-lg-4">

        <div className="frx-split-layout">
          <section className="frx-panel-stack">
            <div className="frx-surface p-3 p-lg-4 rounded-4">
              <div className="frx-section-head">
                <div>
                  <span className="frx-kicker">Wrestler scelti</span>
                </div>
              </div>

              <div className="frx-table-wrap d-none d-lg-block">
                <div className="table-responsive">
                  <table className="table table-dark align-middle">
                    <thead>
                      <tr>
                        <th>Wrestler</th>
                        <th>Brand</th>
                        <th>Prezzo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedWrestlers.map((wrestler) => (
                        <tr key={wrestler.id}>
                          <td>
                            <div className="d-flex align-items-center gap-3">
                              {renderWrestlerImage(wrestler)}
                              <strong className="text-light">{wrestler.name}</strong>
                              <span>
                                {wrestler.gender === 'Uomo' ? (
                                  <i className="bi bi-gender-male text-brand-primary-ultralight" title="Uomo"></i>
                                ) : wrestler.gender === 'Donna' ? (
                                  <i className="bi bi-gender-female text-brand-accent-light" title="Donna"></i>
                                ) : (
                                  <i className="bi bi-gender-ambiguous text-muted-brand" title={wrestler.gender || 'N/A'}></i>
                                )}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className={`${getBrandBadgeClass(wrestler.brand)} badge px-2 py-1`}>
                              {wrestler.brand}
                            </span>
                          </td>
                          <td className="fw-bold text-money">
                            {wrestler.price} <CreditIcon className="ms-1" size={16} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="d-grid gap-3 d-lg-none">
                {selectedWrestlers.map((wrestler) => (
                  <div key={wrestler.id} className="frx-wrestler-card">
                    <div className="d-flex align-items-start gap-3 min-w-0">
                      {/* Immagine wrestler */}
                      {renderWrestlerImage(wrestler, '50px')}

                      {/* Colonna centrale: nome + icona genere e brand */}
                      <div className="flex-grow-1 min-w-0">
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <span className="fw-bold text-light text-truncate">{wrestler.name}</span>
                            {/* Icona genere accanto al nome */}
                            {wrestler.gender === 'Uomo' ? (
                              <i className="bi bi-gender-male text-brand-primary-ultralight" title="Uomo"></i>
                            ) : wrestler.gender === 'Donna' ? (
                              <i className="bi bi-gender-female text-brand-accent-light" title="Donna"></i>
                            ) : (
                              <i className="bi bi-gender-ambiguous text-muted-brand" title={wrestler.gender || 'N/A'}></i>
                            )}
                          </div>
                          {/* Prezzo allineato a destra */}
                          <div className="fw-bold text-money d-flex align-items-center gap-1">
                            {wrestler.price} <CreditIcon size={18} />
                          </div>
                        </div>
                        {/* Brand sotto il nome */}
                        <div className="mt-1">
                          <span className={`${getBrandBadgeClass(wrestler.brand)} badge px-2 py-1`}>
                            {wrestler.brand}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <aside className="frx-summary-sidebar frx-sticky">
            <div className="frx-surface p-3 p-lg-4 rounded-4">
              <div className="frx-section-head">
                <div>
                  <span className="frx-kicker">Salvataggio</span>
                </div>
              </div>

              {error && <div className="alert alert-danger frx-alert">{error}</div>}
              {successMessage && <div className="alert alert-success frx-alert">{successMessage}</div>}
              {!canSubmit && (
                <div className="alert alert-warning frx-alert bg-warning bg-opacity-10 text-light">
                  La selezione non rispetta ancora tutti i limiti richiesti dal roster.
                </div>
              )}

              <div className="frx-form-block">
                <label htmlFor="desktopFactionName" className="form-label fw-semibold text-light">Nome della fazione</label>
                <input
                  type="text"
                  className={`form-control form-control-lg ${error ? 'is-invalid' : ''}`}
                  id="desktopFactionName"
                  placeholder="Es: FantaFaction"
                  value={factionName}
                  onChange={(event) => setFactionName(event.target.value)}
                />
              </div>

              <div className="frx-divider"></div>

              <div className="row row-cols-2 g-3">
                <div className="col">
                  <InfoCard title="Modalità" value={gameMode} icon="bi-controller" />
                </div>
                <div className="col">
                  <InfoCard title="Membri" value={`${selectedWrestlers.length} wrestler`} icon="bi-people" />
                </div>
                <div className="col">
                  <InfoCard title="Crediti" value={`${creditsLeft}`} icon="bi-coin" />
                </div>
                <div className="col">
                  <InfoCard title="Roster" value={rosterName || 'Nessun roster'} icon="bi-grid-1x2-fill" />
                </div>
              </div>

              <div className="frx-form-block mt-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-light fw-semibold">Completamento fazione</span>
                  <span className="text-brand-secondary fw-bold">{selectedWrestlers.length}/{maxWrestlers}</span>
                </div>
                <div className="progress frx-progress">
                  <div className="progress-bar bg-brand-secondary" style={{ width: `${selectionProgress}%` }}></div>
                </div>
              </div>

              <div className="frx-form-block">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-light fw-semibold">Budget usato</span>
                  <span className={`fw-bold ${creditsLeft < maxCredits * 0.15 ? 'text-danger' : 'text-brand-secondary'}`}>
                    {totalCredits}/{maxCredits}
                  </span>
                </div>
                <div className="progress frx-progress">
                  <div className={`progress-bar ${totalCredits > maxCredits * 0.9 ? 'bg-danger' : 'bg-brand-secondary'}`} style={{ width: `${creditsProgress}%` }}></div>
                </div>
              </div>

              <div className="frx-form-block">
                <span className="frx-label">Distribuzione per brand</span>
                <div className="frx-pill-row">
                  {selectedBrands.length > 0 ? selectedBrands.map(([brand, count]) => (
                    <span key={brand} className={`badge rounded-pill px-3 py-2 ${getBrandBadgeClass(brand)}`}>
                      {brand}: {count}
                    </span>
                  )) : (
                    <small className="text-muted-brand">Nessun wrestler selezionato.</small>
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-between gap-2 mt-4">
                <button className="btn btn-outline-brand-secondary" onClick={onBack} disabled={isSubmitting}>
                  <i className="bi bi-arrow-left me-2"></i>
                  Modifica selezione
                </button>
                <button className="btn btn-brand-secondary" onClick={handleSubmit} disabled={isSubmitting || !canSubmit}>
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      Inizia a giocare <i className="bi bi-play-fill ms-2"></i>
                    </>
                  )}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <style>{`
        .overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0,0,0,0.7);
          z-index: 9999;
        }
      `}</style>
    </div>
  );
};

export default FactionSummary;
